import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Info Cobranzas Proyectadas
 * Define las columnas y el formato específico con columnas dinámicas de meses
 */
export default class InfoCobranzasProyectadasExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Info Cobranzas Proyectadas');
  }

  getTitle(context) {
    return 'INFORME DE COBRANZAS PROYECTADAS';
  }

  /**
   * Define las columnas del reporte de Info Cobranzas Proyectadas
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.legajo || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''  // Vacío - el texto va en Fideicomiso
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomiso || item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'  // Texto aquí
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria_linea',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria_linea || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria_linea || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'nombre_razon_social',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.nombre_razon_social) return '-';
        return item.nombre_razon_social.toUpperCase();
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.cuit || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.persona || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha de Ingreso
    columns.push({
      key: 'fecha_ingreso',
      header: 'Fecha de Ingreso',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_ingreso),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Estado
    columns.push({
      key: 'estado',
      header: 'Estado',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.estado || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 10. Período
    columns.push({
      key: 'periodo',
      header: 'Período',
      pdfWidth: 22,
      excelWidth: 16,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const periodo = parseFloat(item.periodo) || 0;
          return total + periodo;
        }, 0);
      }
    });

    // 11-N. Columnas dinámicas de meses
    const columnasMeses = this.getColumnasMeses(options);
    console.log(`📊 Info Cobranzas Proyectadas - Generando ${columnasMeses.length} columnas de meses`);

    columnasMeses.forEach(columnaMes => {
      columns.push({
        key: columnaMes.value,
        header: columnaMes.text,
        pdfWidth: 20,
        excelWidth: 14,
        align: 'right',

        format: (item, ctx) => {
          const value = parseFloat(item[columnaMes.value]) || 0;
          return this.formatMonto(value);
        },

        formatSubtotal: (item, ctx) => {
          // Para subtotales, buscar en el array de subtotales
          const value = this.getSubtotalParaColumna(item, columnaMes.value);
          return this.formatMonto(value);
        },

        calculateTotal: (items, ctx) => {
          return items.reduce((total, item) => {
            const valor = parseFloat(item[columnaMes.value]) || 0;
            return total + valor;
          }, 0);
        }
      });
    });

    return columns;
  }

  /**
   * Obtiene las columnas de meses dinámicas basándose en los datos
   */
  getColumnasMeses(options) {
    // Si se pasan las columnas de meses en options, usarlas
    if (options.columnasMeses && Array.isArray(options.columnasMeses)) {
      console.log(`✅ Info Cobranzas Proyectadas - Usando ${options.columnasMeses.length} columnas de meses del contexto`);
      return options.columnasMeses;
    }

    // Si se pasa el contexto con datos, calcularlas
    if (options.datos && Array.isArray(options.datos)) {
      console.log('📊 Info Cobranzas Proyectadas - Calculando columnas de meses desde datos');
      return this.calcularColumnasMesesDesdeDatos(options.datos);
    }

    console.warn('⚠️ Info Cobranzas Proyectadas - No se encontraron columnas de meses en options ni datos');
    return [];
  }

  /**
   * Calcula las columnas de meses basándose en los datos reales
   * Los datos ya vienen procesados con propiedades fecha_MES_AÑO
   */
  calcularColumnasMesesDesdeDatos(datos) {
    const meses = {
      1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
      7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
    };

    const periodosConAño = new Map();

    // Detectar períodos desde las propiedades procesadas fecha_MES_AÑO
    datos.forEach(item => {
      if (!item._esSubtotal) {
        // Buscar todas las propiedades que empiecen con "fecha_" y terminen con "_YYYY"
        Object.keys(item).forEach(key => {
          if (key.startsWith('fecha_') && item[key] && item[key] > 0) {
            // Extraer mes y año del formato fecha_MES_AÑO
            const partes = key.split('_');
            if (partes.length === 3) {
              const mes = parseInt(partes[1], 10);
              const año = parseInt(partes[2], 10);

              if (mes >= 1 && mes <= 12 && año > 2000) {
                // Crear clave única por mes-año para ordenamiento
                const clave = `${año}-${mes.toString().padStart(2, '0')}`;
                periodosConAño.set(clave, { mes, año });
              }
            }
          }
        });
      }
    });

    const fechasHeaders = [];

    // Generar headers ordenados cronológicamente
    if (periodosConAño.size > 0) {
      Array.from(periodosConAño.keys())
        .sort()
        .forEach(clave => {
          const { mes, año } = periodosConAño.get(clave);
          const añoCorto = año.toString().slice(-2);
          fechasHeaders.push({
            text: `${meses[mes]} ${añoCorto}`,
            value: `fecha_${mes}_${año}`
          });
        });
    }

    console.log(`📊 Info Cobranzas Proyectadas - Calculadas ${fechasHeaders.length} columnas de meses`);
    return fechasHeaders;
  }

  /**
   * Obtiene el subtotal para una columna de mes específica
   */
  getSubtotalParaColumna(item, columnValue) {
    if (!item._esSubtotal || !item.subtotales) {
      return 0;
    }

    // columnValue tiene formato "fecha_2_2026", extraer mes y año
    const partes = columnValue.split('_');
    if (partes.length !== 3) return 0;

    const mes = parseInt(partes[1], 10);
    const año = parseInt(partes[2], 10);

    // Buscar en el array de subtotales el que coincida con mes-año
    const subtotal = item.subtotales.find(sub => {
      // El mesAño viene como "MM-YYYY"
      const [mesStr, añoStr] = sub.mesAño.split('-');
      const mesNum = parseInt(mesStr, 10);
      const añoNum = parseInt(añoStr, 10);
      return mesNum === mes && añoNum === año;
    });

    return subtotal ? subtotal.totalMes : 0;
  }

  /**
   * Info Cobranzas Proyectadas no usa desembolsos
   */
  getMaxDesembolsos(options) {
    return 0;
  }
}
